import React from 'react';

import { Box, Typography } from '@material-ui/core';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

import { TeamsReportRequest, TeamsReportStatus } from '../../../api/reports/teamsReports';
import { AppButton } from '../../../components/AppButton';
import { SettingsStatusPill, SettingsStatusTone } from '../../../components/SettingsCard/SettingsStatusPill';
import { formatUtcToUkDate } from '../../../utils/dates';
import { ANALYSIS_LABELS, REPORTS_ROUTE, REPORT_TYPE_LABELS, STATUS_LABELS } from '../constants';
import { useReportStyles } from '../styles';
import { formatReportSize, getReportTitle, getScopeLabel } from '../utils';

export const STATUS_TONES: Record<TeamsReportStatus, SettingsStatusTone> = {
  pending: 'info',
  running: 'info',
  completed: 'on',
  failed: 'error',
  expired: 'off',
};

const DATE_TIME: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/** Naive-UTC timestamp from the gateway, shown as UK wall-clock time. */
export const formatReportDate = (date: string | null | undefined, withTime = true) =>
  formatUtcToUkDate(date, withTime ? DATE_TIME : { day: 'numeric', month: 'long', year: 'numeric' });

interface Props {
  report: TeamsReportRequest;
  canDelete: boolean;
  downloading: boolean;
  deleting: boolean;
  onDownload: () => void;
  onDelete: () => void;
  /** Failed or expired: start a fresh request in the wizard. */
  onRequestAgain: () => void;
}

export const ReportRow: React.FC<Props> = ({
  report,
  canDelete,
  downloading,
  deleting,
  onDownload,
  onDelete,
  onRequestAgain,
}) => {
  const classes = useReportStyles();
  const size = formatReportSize(report.size_bytes);

  return (
    <Box className={classes.card}>
      <Box className={classes.cardMain}>
        <Box display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
          <Typography
            component={RouterLink}
            to={`${REPORTS_ROUTE}/${report.request_id}`}
            className={classes.cardTitle}
            style={{ textDecoration: 'none' }}>
            {getReportTitle(report)}
          </Typography>
          <SettingsStatusPill label={STATUS_LABELS[report.status]} tone={STATUS_TONES[report.status]} />
          {report.contains_pii && <SettingsStatusPill label="Contains personal data" tone="warning" />}
        </Box>
        <Box className={classes.cardMeta}>
          <span>{REPORT_TYPE_LABELS[report.report_type]}</span>
          <span>Scope: {getScopeLabel(report)}</span>
          <span>{ANALYSIS_LABELS[report.analysis_mode]}</span>
          <span>
            By {report.requested_by.name}
            {report.requested_by.actor_type === 'legend' ? ' (Vyce)' : ''} · {formatReportDate(report.created_at)}
          </span>
          {size && <span>{size}</span>}
        </Box>
        {report.status === 'completed' && report.error_code === 'analysis_failed' && (
          <Typography className={classes.hint} style={{ marginTop: 4 }}>
            Analysis unavailable — the report is complete without it.
          </Typography>
        )}
        {report.status === 'failed' && (
          <Typography className={classes.hint} style={{ marginTop: 4 }}>
            We couldn’t generate this report. Support reference: {report.error_code || 'unknown'}.
          </Typography>
        )}
      </Box>

      <Box className={classes.cardActions}>
        {report.status === 'completed' && (
          <AppButton
            variant="contained"
            color="primary"
            size="small"
            startIcon={<FiDownload size={14} />}
            loading={downloading}
            loaderVariant="paper"
            onClick={onDownload}>
            Download
          </AppButton>
        )}
        {(report.status === 'failed' || report.status === 'expired') && (
          <AppButton variant="outlined" color="primary" size="small" onClick={onRequestAgain}>
            {report.status === 'failed' ? 'Retry' : 'Generate again'}
          </AppButton>
        )}
        {canDelete && (
          <AppButton
            variant="outlined"
            size="small"
            aria-label={`Delete ${getReportTitle(report)}`}
            loading={deleting}
            onClick={onDelete}
            startIcon={<FiTrash2 size={14} />}>
            Delete
          </AppButton>
        )}
      </Box>
    </Box>
  );
};
